import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';

interface Props {
  currentPage: number;
  disabled?: boolean;
  numberOfPages: number;
  onPageChange: ({ page }: { page: number }) => void | Promise<void>;
  style?: React.CSSProperties;
}

const Pagination = ({
  currentPage,
  disabled,
  numberOfPages,
  onPageChange,
  style
}: Props) => {
  const { messages, theme } = useReduxState();

  if (!numberOfPages) return null;

  const buttons: (string | number)[] = [1];
  if (currentPage > 3 && numberOfPages > 4) buttons.push('separator');
  if (currentPage === numberOfPages && numberOfPages > 3)
    buttons.push(currentPage - 2);
  if (currentPage > 2) buttons.push(currentPage - 1);
  if (currentPage > 1 && currentPage < numberOfPages) buttons.push(currentPage);
  if (currentPage < numberOfPages - 1) buttons.push(currentPage + 1);
  if (currentPage === 1 && numberOfPages > 3) buttons.push(3);
  if (currentPage < numberOfPages - 2 && numberOfPages > 4)
    buttons.push('separator');
  if (numberOfPages > 1) buttons.push(numberOfPages);

  return (
    <div className="pagination" style={style}>
      <button
        aria-label={messages.paginationPrevious}
        className="button focus-box-shadow"
        disabled={disabled || currentPage === 1}
        onClick={() => onPageChange({ page: currentPage - 1 })}
      >
        <Icon icon="chevron-left" />
      </button>
      {buttons.map((page, i) => {
        return Number.isInteger(page) ? (
          <button
            aria-label={`${messages.paginationPage} ${page}`}
            className={
              `button focus-box-shadow` +
              `${page === currentPage ? ' active' : ''}`
            }
            disabled={disabled || page === currentPage}
            key={i}
            onClick={() => onPageChange({ page: +page })}
          >
            {page}
          </button>
        ) : (
          <div aria-hidden="true" className="separator" key={i}>
            ...
          </div>
        );
      })}

      <button
        aria-label={messages.paginationNext}
        className="button focus-box-shadow"
        disabled={disabled || currentPage === numberOfPages}
        onClick={() => onPageChange({ page: currentPage + 1 })}
      >
        <Icon icon="chevron-right" />
      </button>

      <style jsx>{`
        .pagination {
          margin-top: 20px;
          text-align: center;
        }
        .button {
          background-color: transparent;
          border: 0;
          border-radius: 32px;
          cursor: pointer;
          color: ${theme.pageTextColorLighter || theme.textColorLighter};
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 700;
          height: 32px;
          outline: 0;
          padding: 0;
          text-align: center;
          transition: background-color 0.3s;
          width: 32px;
        }
        .button:disabled {
          cursor: default;
          opacity: 0.5;
        }
        .button.active:disabled {
          opacity: 1;
        }
        .button:hover:not(:disabled) {
          background-color: ${theme.lightGreyDark};
        }
        .button:focus {
          box-shadow: 0 0 0 5px rgba(0, 0, 0, 0.15);
        }
        .button.active {
          background-color: ${theme.mainColor};
          color: ${theme.mainTextColor};
        }
        .pagination .separator {
          color: ${theme.pageTextColorLighter || theme.textColorLighter};
          cursor: default;
          display: inline-block;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 700;
          height: 34px;
          padding: 8px 0;
          text-align: center;
          width: 24px;
        }
        .button:not(:first-child),
        .pagination .separator {
          margin-left: 5px;
        }
      `}</style>
    </div>
  );
};

export default Pagination;
